import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Container, CircularProgress, Alert, Grid, Avatar, Stack } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { debounce } from 'lodash';
import WbTextField from '../../../components/common/WbTextField';
import WbButton from '../../../components/common/WbButton';
import WbLoader from '../../../components/common/WbLoader';
import KioskLayout from '../../../components/kiosk/KioskLayout';
import { useKiosk } from '../../../contexts/KioskContext';
import kioskApi from '../../../services/api/kiosk/kioskApi';
import paths from '../../../routes/paths';
import '../KioskStyles.scss';

export default function ParentSearch() {
  const { schoolSlug } = useParams();
  const { school, loading: schoolLoading } = useKiosk();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);

  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3) {
      setResults([]);
      return;
    }

    setSearching(true);
    try {
      const data = await kioskApi.searchParents(schoolSlug, searchQuery);
      setResults(data || []);
    } catch (err) {
      console.error('Search error:', err);
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [schoolSlug]);

  const debouncedSearch = useCallback(
    debounce((searchQuery) => {
      performSearch(searchQuery);
    }, 300),
    [performSearch]
  );

  useEffect(() => {
    if (query.length >= 3) {
      debouncedSearch(query);
    } else {
      setResults([]);
    }
  }, [query, debouncedSearch]);

  const handleSelectChild = (child) => {
    setSelectedChild(child);
    navigate(paths.kioskParentVerify.replace(':schoolSlug', schoolSlug), {
      state: { child }
    });
  };

  if (schoolLoading) {
    return (
      <KioskLayout>
        <WbLoader />
      </KioskLayout>
    );
  }

  return (
    <KioskLayout>
      <Container maxWidth="md">
        <div className="kiosk-title">
          Search for Your Child
        </div>
        <div className="kiosk-subtitle">
          Type at least 3 letters to search
        </div>

        <Box sx={{ mt: 4 }}>
          <WbTextField
            label="Child's Name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}            
            autoFocus
          />
          {searching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>

        {results.length > 0 && (
          <Grid container spacing={2} mt={2}>
            {results.map((result) => (
              <Grid item xs={12} sm={6} md={4}>
                <div
                  key={result.id}
                  className="kiosk-result-item"
                  onClick={() => handleSelectChild(result)}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar>{result.childName ? result.childName.charAt(0) : ''}</Avatar>
                    <div>
                      <Typography className="result-name">{result.childName}</Typography>
                      <Typography className="result-class">Reg No: {result.childRegistrationClass}</Typography>
                    </div>
                  </Stack>
                </div>
              </Grid>
            ))}
          </Grid>
        )}

        {query.length > 0 && query.length < 3 && (
          <Alert severity="info" sx={{ mt: 2 }}>Please type at least 3 letters</Alert>
        )}

        {query.length >= 3 && results.length === 0 && !searching && (
          <Alert severity="info" sx={{ mt: 2 }}>No results found</Alert>
        )}
      </Container>
    </KioskLayout>
  );
}
